/** 插件预设工厂：提供不同场景的 Plugin 组合 */

import type { Plugin } from '@/shared/types/plugin'
import { AnimationPlugin } from './animation/AnimationPlugin'
import { CadPlugin } from './cad/CadPlugin'
import { CameraPlugin } from './camera/CameraPlugin'
import { corePlugins, type PluginPresetOptions } from './corePlugins'
import { GaussianSplatPlugin } from './gsplat/GSplatPlugin'
import { PickingPlugin } from './picking/PickingPlugin'
import { BoundingBoxOverlayPlugin } from './pointCloud/BoundingBoxOverlayPlugin'
import { PointCloudPlugin } from './pointCloud/PointCloudPlugin'
import { RenderPlugin } from './render/RenderPlugin'
import { AnnotationPlugin } from './tools/annotation/AnnotationPlugin'
import { ClipOverlayPlugin } from './tools/clip/ClipOverlayPlugin'
import { ClipPlugin } from './tools/clip/ClipPlugin'
import { MeasureOverlayPlugin } from './tools/measure/MeasureOverlayPlugin'
import { MeasurePlugin } from './tools/measure/MeasurePlugin'
import { ProfileOverlayPlugin } from './tools/profile/ProfileOverlayPlugin'
import { ProfilePlugin } from './tools/profile/ProfilePlugin'
import { ScreenBoxSelectOverlayPlugin } from './tools/screenBoxSelect/ScreenBoxSelectOverlayPlugin'
import { ScreenBoxSelectPlugin } from './tools/screenBoxSelect/ScreenBoxSelectPlugin'
import { TransformPlugin } from './tools/transform/TransformPlugin'
import { VolumeOverlayPlugin } from './tools/volume/VolumeOverlayPlugin'
import { VolumePlugin } from './tools/volume/VolumePlugin'
import { VideoRenderPlugin } from './videoRender/VideoRenderPlugin'
import { ViewPlugin } from './view/ViewPlugin'

export type { PluginPresetOptions }

export interface VideoRenderPluginPresetOptions extends PluginPresetOptions {
  additionalPlugins?: Plugin[]
}

/**
 * 全功能引擎：Core + 3 Asset + 5 Tool + AnnotationPlugin。
 *
 * @returns 插件实例数组，按注册顺序返回
 */
export function full(options: PluginPresetOptions = {}): Plugin[] {
  return [
    ...corePlugins(options),
    new PointCloudPlugin({ withCredentials: options.withCredentials }),
    new GaussianSplatPlugin({ withCredentials: options.withCredentials }),
    new CadPlugin(),
    new MeasurePlugin(),
    new ClipPlugin(),
    new ProfilePlugin(),
    new VolumePlugin(),
    new TransformPlugin(),
    new AnnotationPlugin(),
  ]
}

/**
 * 仅点云查看：Core + PointCloudPlugin。
 *
 * @returns 插件实例数组，按注册顺序返回
 */
export function pointCloudOnly(options: PluginPresetOptions = {}): Plugin[] {
  return [
    ...corePlugins(options),
    new PointCloudPlugin({ withCredentials: options.withCredentials }),
  ]
}

/**
 * 点云 + 3DGS：pointCloudOnly() + GaussianSplatPlugin。
 *
 * @returns 插件实例数组，按注册顺序返回
 */
export function pointCloudAndGSplat(options: PluginPresetOptions = {}): Plugin[] {
  return [
    ...pointCloudOnly(options),
    new GaussianSplatPlugin({ withCredentials: options.withCredentials }),
  ]
}

/**
 * 全功能引擎 + Overlay：full() + ScreenBoxSelect + 5 Overlay。
 *
 * @returns 插件实例数组，按注册顺序返回
 */
export function fullWithOverlays(options: PluginPresetOptions = {}): Plugin[] {
  return [
    ...full(options),
    new ScreenBoxSelectPlugin(),
    new MeasureOverlayPlugin(),
    new ClipOverlayPlugin(),
    new VolumeOverlayPlugin(),
    new ProfileOverlayPlugin(),
    new ScreenBoxSelectOverlayPlugin(),
    new BoundingBoxOverlayPlugin(),
  ]
}

/**
 * CAD 测绘正交相机：corePlugins + PointCloudPlugin + CadPlugin。
 *
 * @returns 插件实例数组，按注册顺序返回
 */
export function cadOnly(options: PluginPresetOptions = {}): Plugin[] {
  return [
    ...corePlugins(options),
    new PointCloudPlugin({ withCredentials: options.withCredentials }),
    new CadPlugin(),
  ]
}

export function videoRender(options: VideoRenderPluginPresetOptions = {}): Plugin[] {
  return [
    new ViewPlugin(),
    new CameraPlugin(),
    new RenderPlugin(),
    new AnimationPlugin(),
    new PickingPlugin(),
    new GaussianSplatPlugin({ withCredentials: options.withCredentials }),
    new VideoRenderPlugin(),
    ...(options.additionalPlugins ?? []),
  ]
}
